import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { passwordProblemMessage, personalWords, type PasswordProblem } from '@wr/shared';
import { ApiError, del, errorMessage, get, post, put } from '../api.js';
import {
  Field,
  NewPasswordFields,
  Notice,
  PageHead,
  RecoveryCodes,
  formatDateTime,
  newPasswordReady,
} from '../components/ui.js';
import { InstallHelp } from '../components/InstallHelp.js';
import { SignOutButton } from '../components/SignOut.js';
import { useOffline } from '../offline/context.js';
import { refreshMyCard } from '../offline/myCard.js';
import { useSignedIn } from '../session.js';
import { OfflineContent } from './content/OfflineContent.js';

interface BusinessCard {
  jobTitle: string | null;
  mobile: string | null;
  officePhone: string | null;
  linkedinUrl: string | null;
}

interface AccountInfo {
  card: BusinessCard;
  email: string;
  firstName: string;
  lastName: string;
  roles: { key: string; label: string }[];
  appSetUpAt: string | null;
  recoveryCodesLeft: number;
  trustedDeviceDays: number;
  trustedDevices: {
    id: string;
    label: string;
    lastUsedAt: string;
    expiresAt: string;
    current: boolean;
  }[];
}

/** My account: every signed-in user. */
export function Account() {
  const { rules, refresh, sessionExpiresAt, offline } = useSignedIn();
  const navigate = useNavigate();
  const [info, setInfo] = useState<AccountInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [version, setVersion] = useState(0);
  const load = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    let current = true;
    get<AccountInfo>('/account').then(
      (res) => current && setInfo(res),
      (err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      },
    );
    return () => {
      current = false;
    };
  }, [refresh, version]);

  return (
    <>
      <PageHead title="My account" />
      {error && <Notice kind="error">{error}</Notice>}
      {offline && <InstallHelp offlineUntil={sessionExpiresAt} />}
      {info && (
        <>
          <section className="section card">
            <h2 className="sec">
              {info.firstName} {info.lastName}
            </h2>
            <p>{info.email}</p>
            <p className="muted small">
              Roles: {info.roles.map((r) => r.label).join(', ') || 'none'}
            </p>
            <p className="muted small">
              Two-step sign-in with an authenticator app (set up {formatDateTime(info.appSetUpAt)}).
            </p>
          </section>
          <MyCard key={JSON.stringify(info.card)} card={info.card} onDone={load} />
          <ChangePassword info={info} policy={rules.password} />
          <NewRecoveryCodes left={info.recoveryCodesLeft} onDone={load} />
          <TrustedDevices info={info} onChange={load} />
          <InstallHelp offlineUntil={sessionExpiresAt} />
        </>
      )}
      {/* Round 2: what this device keeps for shows without network (it shows offline too). */}
      <OfflineContent />
      <section className="section card" aria-labelledby="so-title">
        <h2 id="so-title" className="sec">
          Sign out
        </h2>
        <p className="note">
          Signing out needs the network. Changes not sent yet stay on this device.
        </p>
        <div className="actions">
          <SignOutButton />
          <button type="button" className="btn" onClick={() => navigate('/sync')}>
            Changes not sent
          </button>
        </div>
      </section>
    </>
  );
}

/** My business card: shown to prospects on the package pages, with its QR code. */
function MyCard({ card, onDone }: { card: BusinessCard; onDone(): void }) {
  const { store } = useOffline();
  const [value, setValue] = useState({
    jobTitle: card.jobTitle ?? '',
    mobile: card.mobile ?? '',
    officePhone: card.officePhone ?? '',
    linkedinUrl: card.linkedinUrl ?? '',
  });
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (key: keyof typeof value, v: string) => setValue({ ...value, [key]: v });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      await put('/account/card', value);
      // The card opened from the name at the top shows it at once, also offline.
      if (store) await refreshMyCard(store);
      setMessage({ kind: 'ok', text: 'Business card saved.' });
      onDone();
    } catch (err) {
      setMessage({
        kind: 'error',
        text:
          err instanceof ApiError && err.code === 'invalid_request'
            ? 'Check the LinkedIn link: it starts with https://www.linkedin.com/.'
            : errorMessage(err),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="section card" aria-labelledby="bc-title">
      <h2 id="bc-title" className="sec">
        My business card
      </h2>
      <p className="note">
        Opened from your name at the top of the screen, for a visitor to scan, and shown to
        prospects on the pages of your packages, with a QR code.
      </p>
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <form onSubmit={(e) => void submit(e)} className="vstack">
        <Field
          label="Job title"
          value={value.jobTitle}
          onChange={(e) => set('jobTitle', e.target.value)}
        />
        <div className="g2">
          <Field
            label="Mobile"
            type="tel"
            autoComplete="tel"
            value={value.mobile}
            onChange={(e) => set('mobile', e.target.value)}
          />
          <Field
            label="Office phone"
            type="tel"
            value={value.officePhone}
            onChange={(e) => set('officePhone', e.target.value)}
          />
        </div>
        <Field
          label="LinkedIn"
          type="url"
          placeholder="https://www.linkedin.com/in/..."
          value={value.linkedinUrl}
          onChange={(e) => set('linkedinUrl', e.target.value)}
        />
        <button type="submit" className="btn dark" disabled={busy}>
          Save my card
        </button>
      </form>
    </section>
  );
}

function ChangePassword({
  info,
  policy,
}: {
  info: AccountInfo;
  policy: { minLength: number; maxLength: number };
}) {
  const [current, setCurrent] = useState('');
  const [value, setValue] = useState({ password: '', confirm: '' });
  const [serverProblems, setServerProblems] = useState<string[]>([]);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const personal = personalWords(info);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    setServerProblems([]);
    try {
      await post('/account/password', { currentPassword: current, newPassword: value.password });
      setCurrent('');
      setValue({ password: '', confirm: '' });
      setMessage({
        kind: 'ok',
        text: 'Password changed. Your other sessions are signed out, and trusted devices will ask for a code again.',
      });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'weak_password') {
        const problems = (err.body.problems as PasswordProblem[] | undefined) ?? [];
        setServerProblems(problems.map((p) => passwordProblemMessage(p, policy)));
      } else if (err instanceof ApiError && err.code === 'invalid_credentials') {
        setMessage({ kind: 'error', text: 'Your current password is incorrect.' });
      } else {
        setMessage({ kind: 'error', text: errorMessage(err) });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="section card" aria-labelledby="pw-title">
      <h2 id="pw-title" className="sec">
        Change my password
      </h2>
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <form onSubmit={(e) => void submit(e)} className="vstack">
        <Field
          label="Current password"
          type="password"
          autoComplete="current-password"
          required
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <NewPasswordFields
          policy={policy}
          personal={personal}
          value={value}
          onChange={setValue}
          serverProblems={serverProblems}
        />
        <button
          type="submit"
          className="btn dark"
          disabled={busy || current === '' || !newPasswordReady(value, policy, personal)}
        >
          Change my password
        </button>
      </form>
    </section>
  );
}

function NewRecoveryCodes({ left, onDone }: { left: number; onDone(): void }) {
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await post<{ recoveryCodes: string[] }>('/account/recovery-codes', { code });
      setCodes(res.recoveryCodes);
      setCode('');
      onDone();
    } catch (err) {
      setCode('');
      setError(
        err instanceof ApiError && err.code === 'invalid_code'
          ? 'This code is not valid. Enter the code your app shows now.'
          : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="section card" aria-labelledby="rc-title">
      <h2 id="rc-title" className="sec">
        Recovery codes
      </h2>
      {codes ? (
        <>
          <Notice kind="ok">
            Here are your new recovery codes. The old ones no longer work. Save these now: they are
            shown only once.
          </Notice>
          <RecoveryCodes codes={codes} />
        </>
      ) : (
        <>
          <p>
            <strong>{left}</strong> {left === 1 ? 'code' : 'codes'} left. Making new codes cancels
            the old ones.
          </p>
          {error && <Notice kind="error">{error}</Notice>}
          <form onSubmit={(e) => void submit(e)} className="vstack">
            <Field
              label="Code of your authenticator app"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9 ]{6,7}"
              maxLength={7}
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <button type="submit" className="btn dark" disabled={busy}>
              Make new recovery codes
            </button>
          </form>
        </>
      )}
    </section>
  );
}

function TrustedDevices({ info, onChange }: { info: AccountInfo; onChange(): void }) {
  const [error, setError] = useState<string | null>(null);
  if (info.trustedDeviceDays === 0) return null;

  async function remove(id: string) {
    setError(null);
    try {
      await del(`/account/trusted-devices/${id}`);
      onChange();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <section className="section card" aria-labelledby="td-title">
      <h2 id="td-title" className="sec">
        Trusted devices
      </h2>
      <p className="muted small">
        On a trusted device, only your password is asked for {info.trustedDeviceDays} days.
      </p>
      {error && <Notice kind="error">{error}</Notice>}
      {info.trustedDevices.length === 0 ? (
        <p>No trusted device.</p>
      ) : (
        <ul className="list" aria-label="Trusted devices">
          {info.trustedDevices.map((device) => (
            <li key={device.id} className="row static">
              <span className="tx">
                <span className="t1">
                  {device.label}
                  {device.current && <span className="badge b-ok"> This device</span>}
                </span>
                <span className="t2">
                  Last used {formatDateTime(device.lastUsedAt)}, until{' '}
                  {formatDateTime(device.expiresAt)}
                </span>
              </span>
              <button type="button" className="btn sm" onClick={() => void remove(device.id)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
